import { ReactNode } from "react";
import { FieldValues } from "react-hook-form";

import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import CheckboxFormField from "components/checkbox-form-field";
import { RhfCheckboxFormFieldProps } from "components/checkbox-form-field/checkbox-form-field.component";

type LabeledCheckboxFieldProps<T extends FieldValues> =
  RhfCheckboxFormFieldProps<T> & {
    label: ReactNode;
    /** Always-visible helper line describing what the toggle does. */
    description?: ReactNode;
    /** Applied to the outer FormControlLabel wrapper. */
    wrapperSx?: SxProps;
  };

/**
 * A checkbox with a label and an always-visible description beneath it.
 * Wraps {@link CheckboxFormField} so all react-hook-form wiring (`name`,
 * `control`, `testId`, `onClick`, `disabled`) is preserved while adding a
 * muted sub-label explaining what enabling the toggle grants.
 */
export default function LabeledCheckboxField<T extends FieldValues>({
  label,
  description,
  wrapperSx,
  ...checkboxProps
}: LabeledCheckboxFieldProps<T>) {
  return (
    <FormControlLabel
      sx={{ alignItems: "flex-start", m: 0, ...wrapperSx }}
      control={
        <CheckboxFormField
          {...checkboxProps}
          sx={{ pt: 0.25, ...checkboxProps.sx }}
        />
      }
      label={
        <Box sx={{ pt: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1.5, mt: 0.25 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      }
    />
  );
}
